import { FormEvent, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchSupportContext, submitSupportTicket, type SupportContext } from "../../shared/api/support";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { PageScaffold } from "../../shared/ui/PageScaffold";

const roleLabels: Record<string, string> = {
  admin: "管理者",
  teacher: "講師",
  parent: "保護者",
  student: "生徒"
};

type SubmitState = "idle" | "sending" | "success" | "error";

export function SupportPage() {
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { routeStatus, sessionStatus, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [context, setContext] = useState<SupportContext | null>(null);
  const [contextError, setContextError] = useState("");
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [submitMessage, setSubmitMessage] = useState("");
  const requestId = useRef("");

  useEffect(() => {
    if (!isSessionReady) return;
    let cancelled = false;
    fetchSupportContext(tenantSlug)
      .then((next) => { if (!cancelled) setContext(next); })
      .catch((error: unknown) => {
        if (!cancelled) setContextError(error instanceof Error ? error.message : "サポート情報を取得できませんでした。");
      });
    return () => { cancelled = true; };
  }, [isSessionReady, tenantSlug]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!context || submitState === "sending") return;
    const form = event.currentTarget;
    const data = new FormData(form);
    if (!requestId.current) requestId.current = crypto.randomUUID();
    setSubmitState("sending");
    setSubmitMessage("");
    try {
      const result = await submitSupportTicket(tenantSlug, {
        requestId: requestId.current,
        category: String(data.get("category") ?? ""),
        name: String(data.get("name") ?? ""),
        replyEmail: String(data.get("replyEmail") ?? ""),
        message: String(data.get("message") ?? "")
      });
      form.reset();
      requestId.current = "";
      setSubmitState("success");
      setSubmitMessage(`${result.message} 受付番号：${result.ticketId}`);
    } catch (error) {
      requestId.current = "";
      setSubmitState("error");
      setSubmitMessage(error instanceof Error ? error.message : "送信できませんでした。時間をおいてお試しください。");
    }
  }

  const roleText = context
    ? [context.isTenantOwner ? "テナントオーナー" : null, ...context.roles.map((role) => roleLabels[role] ?? role)]
        .filter(Boolean)
        .join("・")
    : "";

  return (
    <PageScaffold
      title="サポート"
      description="LINE認証済みの所属スクールと役割を自動で付加して、サポートへ送信します。"
      links={[{ to: "/t/:tenantSlug", label: "トップへ戻る" }]}
    >
      <div style={{ display: "grid", gap: 18 }}>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {routeStatus === "redirecting" ? <p>LINEログイン画面へ移動しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}
        {contextError ? <p role="alert" style={{ color: "#a33124" }}>{contextError}</p> : null}

        {context ? (
          <>
            <div style={{ padding: 14, borderRadius: 10, background: "#f6f8fc", display: "grid", gap: 5 }}>
              <strong>{context.tenant.displayName}</strong>
              <span style={{ fontSize: 13, opacity: 0.78 }}>判定された役割：{roleText || "一般ユーザー"}</span>
              <small>所属・役割はログイン情報からサーバー側で確認され、送信内容へ自動で付加されます。</small>
            </div>

            <form onSubmit={handleSubmit} style={{ display: "grid", gap: 14 }}>
              <label style={{ display: "grid", gap: 6 }}>
                <span>お問い合わせ分類 *</span>
                <select name="category" required defaultValue={context.categories[0]?.value ?? ""}>
                  {context.categories.map((category) => <option key={category.value} value={category.value}>{category.label}</option>)}
                </select>
              </label>
              <label style={{ display: "grid", gap: 6 }}>
                <span>お名前 *</span>
                <input name="name" required minLength={2} maxLength={100} autoComplete="name" />
              </label>
              <label style={{ display: "grid", gap: 6 }}>
                <span>返信先メールアドレス *</span>
                <input name="replyEmail" type="email" required maxLength={254} autoComplete="email" inputMode="email" />
              </label>
              <label style={{ display: "grid", gap: 6 }}>
                <span>お問い合わせ内容 *</span>
                <textarea name="message" required minLength={20} maxLength={3000} rows={8} placeholder="発生した画面、操作手順、表示されたエラー、発生日時などをご記入ください。" />
                <small>パスワード、カード情報、本人確認書類、招待トークンは入力しないでください。</small>
              </label>
              {submitMessage ? (
                <p role="status" style={{ margin: 0, padding: 12, borderRadius: 8, color: submitState === "error" ? "#a33124" : "#236a41", background: submitState === "error" ? "#fff0ed" : "#eaf8ef" }}>
                  {submitMessage}
                </p>
              ) : null}
              <button type="submit" disabled={submitState === "sending"}>
                {submitState === "sending" ? "送信しています…" : "サポートへ送信する"}
              </button>
              <small>この画面にはサポート用メールアドレスを掲載していません。返信は入力したメールアドレスへ届きます。</small>
            </form>
          </>
        ) : null}
      </div>
    </PageScaffold>
  );
}
